// Client-side image pipeline for the article editor: crop (optional) +
// downscale + WebP re-encode happen in the browser via canvas, so the server
// only ever stores small, modern files. Upload goes through Active Storage
// direct upload (same endpoint the inbox composer uses).

import { DirectUpload } from '@rails/activestorage'

export interface CropRect {
  x: number
  y: number
  width: number
  height: number
}

export interface ProcessedImage {
  blob: Blob
  width: number
  height: number
  filename: string
}

const MAX_WIDTH = 1600
const WEBP_QUALITY = 0.85

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not load image'))
    img.src = src
  })
}

function filenameFor(source: File | string): string {
  if (source instanceof File) return source.name.replace(/\.[^.]+$/, '') || 'image'
  const base = decodeURIComponent(source.split('/').pop() ?? 'image')
  return base.replace(/\.[^.]+$/, '') || 'image'
}

/** Crop (when given), downscale to MAX_WIDTH and re-encode as WebP.
 *  Accepts a File or a same-origin URL (e.g. a stored original for re-crop). */
export async function processImage(source: File | string, crop: CropRect | null): Promise<ProcessedImage> {
  const isFile = source instanceof File
  const objectUrl = isFile ? URL.createObjectURL(source) : source
  try {
    const img = await loadImage(objectUrl)
    const region = crop ?? { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight }

    const scale = Math.min(1, MAX_WIDTH / region.width)
    const width = Math.round(region.width * scale)
    const height = Math.round(region.height * scale)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas unavailable')
    ctx.drawImage(img, region.x, region.y, region.width, region.height, 0, 0, width, height)

    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/webp', WEBP_QUALITY))
    if (!blob) throw new Error('WebP encoding failed')

    return { blob, width, height, filename: `${filenameFor(source)}.webp` }
  } finally {
    if (isFile) URL.revokeObjectURL(objectUrl)
  }
}

/** Direct-upload a processed image; resolves to a permanent blob URL. */
export function uploadImage(processed: ProcessedImage): Promise<string> {
  const file = new File([ processed.blob ], processed.filename, { type: 'image/webp' })
  return new Promise((resolve, reject) => {
    const upload = new DirectUpload(file, '/rails/active_storage/direct_uploads')
    upload.create((error, blob) => {
      if (error || !blob) {
        reject(error ?? new Error('Upload failed'))
      } else {
        // Proxy (not redirect) so the CDN can cache the image. See BC-104.
        resolve(`/rails/active_storage/blobs/proxy/${blob.signed_id}/${encodeURIComponent(processed.filename)}`)
      }
    })
  })
}
